import { ObjectId } from '@racemap/sdk/schema/base';
import { uuid4 } from '@racemap/utilities/functions/utils';
import RacemapMQTTClient from '@racemap/utilities/rm-mqtt-client';
import {
  SCAN_SESSION_MESSAGE_TYPES,
  type ScanSessionMessage,
} from '@racemap/utilities/types/trackers';
import { produce } from 'immer';
import { toast } from 'src/lib/toast';

import type { StoreApi } from 'zustand';
import type { DraftState, State } from '../../reducers';
import {
  testScanSessionConnectionMessage,
  testScanSessionDisconnectionMessage,
  testScanSessionReadMessage,
} from '../trackers_helper';
import type { TrackersActions } from '../trackers_reducers';

// a second start while the first is still connecting joins it instead of opening another client
let pendingStart: Promise<void> | null = null;

const connectSession = async (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
) => {
  const oldMqttClient = get().trackers.mqttClient;
  if (oldMqttClient?.connected) {
    console.info('MQTT Client is already connected. Skip start of new session!');
    return;
  }
  // a client that lost the broker keeps retrying for the old session's topic
  oldMqttClient?.disconnect();

  const sessionId = uuid4();
  const mqttClient = await RacemapMQTTClient.fromWindow({
    clientId: sessionId,
    username: 'trackerScanner',
    password: 'BoCh8DVwohqMWs7DfpDq',
    subscriptionTopics: [`scan/${sessionId}/receiver`],
    onMessage: get().trackers.actions._handleReaderSessionMessage,
  });

  if (!mqttClient.connected) {
    throw new Error('Failed to connect to MQTT Broker!');
  }
  set(
    produce((s: DraftState) => {
      s.trackers.sessionId = sessionId;
      // Immer's recursive Draft<T> mapping over RacemapMQTTClient's internal fields
      // (e.g. wsOptions.headers) isn't structurally assignable from a plain instance.
      s.trackers.mqttClient = mqttClient as typeof s.trackers.mqttClient;
      s.trackers.sessionState = 'CONNECTED';
    }),
  );
};

export const createScanSessionActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<
  TrackersActions,
  | 'startSession'
  | 'stopSession'
  | '_handleReaderSessionMessage'
  | '_handleReaderConnectAttempt'
  | '_handleReadMessage'
  | '_handleReaderDisconnectAttempt'
> => ({
  startSession: () => {
    pendingStart ??= connectSession(set, get).finally(() => {
      pendingStart = null;
    });
    return pendingStart;
  },
  stopSession: async () => {
    get().trackers.mqttClient?.disconnect();

    set(
      produce((s: DraftState) => {
        s.trackers.sessionId = null;
        s.trackers.mqttClient = null;
        s.trackers.sessionState = 'DISCONNECTED';
      }),
    );
  },
  _handleReaderSessionMessage: (topic, payload) => {
    try {
      const sessionId = get().trackers.sessionId;
      if (sessionId == null) return;

      if (topic !== `scan/${sessionId}/receiver`)
        throw new Error('Receive message on wrong topic!');
      const rawMessage = payload.toString('utf-8');
      const message: ScanSessionMessage = JSON.parse(rawMessage);
      if (message.type == null) throw new Error('Receive invalid message!');

      switch (message.type) {
        case SCAN_SESSION_MESSAGE_TYPES.CONNECT:
          testScanSessionConnectionMessage(message);
          get().trackers.actions._handleReaderConnectAttempt(message.readerId, message.readerName);
          break;
        case SCAN_SESSION_MESSAGE_TYPES.READ:
          testScanSessionReadMessage(message);
          get().trackers.actions._handleReadMessage(
            message.readerId,
            new ObjectId(message.trackerId),
          );
          break;
        case SCAN_SESSION_MESSAGE_TYPES.DISCONNECT:
          testScanSessionDisconnectionMessage(message);
          get().trackers.actions._handleReaderDisconnectAttempt(message.readerId);
          break;
        default:
          console.log(`MQTT: Receive unknown message! Payload: ${payload.toString('utf-8')}`);
      }
    } catch (err) {
      console.error(`MQTT: Failed to handle message! ${err}`);
    }
  },
  _handleReaderConnectAttempt: (readerId, readerName) => {
    set(
      produce((s: DraftState) => {
        const connection = s.trackers.connectedReader.get(readerId) || {
          readerId,
          type: 'App',
          state: 'UNKNOWN',
          readerName: undefined,
          connectedAt: new Date(),
        };
        connection.readerId = readerId;
        connection.state = 'CONNECTED';
        connection.readerName = readerName;
        connection.connectedAt = new Date();
        s.trackers.connectedReader.set(readerId, connection);
      }),
    );
    const client = get().trackers.mqttClient;
    if (client == null) return;

    client.sendScanSessionAcceptConnectMessage(readerId);
    toast.info(`Reader ${readerName} connected!`);
  },
  _handleReadMessage: (readerId, trackerId) => {
    const client = get().trackers.mqttClient;
    if (client == null) return;

    try {
      const connections = get().trackers.connectedReader;
      const sampleTrackerIds = get().trackers.sampleTrackerIds;
      const trackers = get().trackers.items;

      if (!connections.has(readerId))
        throw new Error(`Receive read from a unknown reader ${readerId}.`);

      if (sampleTrackerIds.has(trackerId.toHexString())) {
        throw new Error('Tracker already added');
      }
      const tracker = trackers.get(trackerId.toHexString());
      if (tracker == null) {
        throw new Error('Unknown tracker!');
      }

      console.log(`Reader with id ${readerId} read tracker: ${tracker.trackerName || trackerId}!`);
      get().trackers.actions.moveTrackersToSample([trackerId]);
      client.sendScanSessionAcceptReadMessage(readerId, trackerId.toHexString());
      toast.success(`Added Tracker ${tracker.trackerName || trackerId}.`);
    } catch (err) {
      if (err instanceof Error) {
        if (err.message !== 'Tracker already added') console.error(err);
        client.sendScanSessionReadFailMessage(readerId, trackerId.toHexString(), err.message);
      }
    }
  },
  _handleReaderDisconnectAttempt: (readerId) => {
    const client = get().trackers.mqttClient;
    if (client == null) return;
    try {
      const connections = get().trackers.connectedReader;
      const reader = connections.get(readerId);

      if (!connections.has(readerId))
        throw new Error(`Receive disconnect from a unknown reader ${readerId}.`);
      set(
        produce((s: DraftState) => {
          const connection = s.trackers.connectedReader.get(readerId);
          if (connection == null) return;

          connection.readerId = readerId;
          connection.state = 'DISCONNECTED';
          s.trackers.connectedReader.set(readerId, connection);
        }),
      );
      client.sendScanSessionAcceptDisconnectMessage(readerId);
      toast.info(`Reader ${reader?.readerName || readerId} disconnected!`);
    } catch (err) {
      if (err instanceof Error) {
        console.error(err);
      }
    }
  },
});
